import React, { useState, useEffect, useRef, useMemo } from "react";
import { PropTypes } from "prop-types";
import { useNavigate } from "react-router-dom";
import reactWrapper from "@harbor/elements/utils/react/wrapper";

import AppHOC from "../../../generics/AppHOC";
import Spinner from "../../../common/Spinner";
import ErrorComponent from "../../../common/ErrorComponent";
import DashletTable from "../../../common/DashletTable";
import { getThemeColors } from "../../../utils/colors";
import { getDonutText, getDonutData } from "../../../utils/views/overview";
import { useI18n } from "../../../i18n/useI18n";
import css from "../overviewMagneticStyle.less";
import {
  circuitNameClick,
  exportCsvZip,
  setLocalStorageFlag,
} from "../../../utils/common";
import { getBWFAccess } from "../../../utils/license";
import availTableConfig from "./config/table/circuitAvailTableConfig";
import availDonutConfig from "./config/donut/circuitAvailDonutConfig";
import timeFilterConfig from "../../../generics/config/time-filter-config";
import { health } from "../../circuits/common/masterTableUtil";
import { defaultCellValue, routeURLs } from "../../../utils/enums";
import { displayDateTime } from "../../../utils/displayTime";
import { formatNumber } from "../../../utils/format";
import HarborDonut from "../../../common/HbrDonut";
import QoeListComponent from "../../../common/QoeListComponent";
import { COLOR } from "../../../utils/enums";
import {
  columnsCircuitName,
  getSourceCircuit,
} from "./config/table/harborTableHeaderConfig";

const MAX_ROWS = 5;

const [HbrButton, HbrCard, HbrIconButton, HbrTooltip] = reactWrapper([
  "hbr-button",
  "hbr-card",
  "hbr-icon-button",
  "hbr-tooltip",
]);

const CircuitView = ({
  globalFilter,
  reporting = false,
  reportData = {
    loader: true,
    error: null,
    data: [],
    query: { timeFilterType: "" },
  },
  reportName,
}) => {
  const { tViews } = useI18n("views");
  const navigate = useNavigate();
  const [loader, setLoader] = useState(true);
  const [selectedSiteId, setselectedSiteId] = useState(
    globalFilter.selectedSite,
  );
  const [tableData, setTableData] = useState([]);
  const dataRef = useRef({
    donut: { good: 0, fair: 0, bad: 0, unknown: 0 },
    donutThemecolors: getThemeColors(availDonutConfig.theme),
    donutData: [],
    donutText: [],
    tableConfig: availTableConfig,
    tableTopConfig: {
      ...availTableConfig,
      title: tViews("topAvailableCircuits"),
    },
    chartTooltipConfig: {
      labels: [
        {
          key: "current",
          label: tViews("circuitViewAvailability"),
        },
        {
          key: "previous",
          label: "",
        },
      ],
      colors: getThemeColors(availTableConfig.legend.theme),
      type: "number",
      format: "percent",
    },
    topCircuits: [],
  });
  const bwfAccess = useMemo(() => getBWFAccess(), []);
  const isCircuitViewLoading =
    loader ||
    selectedSiteId !== globalFilter.selectedSite ||
    (reporting && reportData.loader) ||
    (!reporting && globalFilter.circuitsMasterTable.loader);
  const hasDonutData = Object.values(dataRef.current.donut).some(
    (value) => Number(value) > 0,
  );
  const canExportCsv =
    (hasDonutData || tableData?.length > 0) && !isCircuitViewLoading;

  useEffect(() => {
    if (!reporting)
      dataRef.current.chartTooltipConfig.labels[1].label =
        `${tViews("applicationOverviewLabelAvailabilityOn")} ` +
        timeFilterConfig[globalFilter.timePeriodSelected].toolTip;
  }, [globalFilter.timePeriodSelected]);

  useEffect(() => {
    if (
      (!reporting && globalFilter.circuitsMasterTable.loader) ||
      (reporting && reportData.loader)
    ) {
      setLoader(true);
      return;
    }
    const circuitsData = reporting
      ? reportData
      : globalFilter.circuitsMasterTable;
    if (reporting) dataRef.current.error = reportData.error;
    else dataRef.current.error = circuitsData.error;
    if (dataRef.current.error) {
      setLoader(false);
      return;
    }
    Object.assign(dataRef.current.donut, {
      good: 0,
      fair: 0,
      bad: 0,
      unknown: 0,
    });
    let data = circuitsData.data;
    if (data.length > 0) {
      let good = 0;
      let fair = 0;
      let bad = 0;
      let unknown = 0;
      const siteId = globalFilter.selectedSite;
      if (siteId !== -1) data = data.filter((item) => item.site_id === siteId);
      data.forEach((item) => {
        const availability = parseInt(item.availability);
        if (availability < 80) bad++;
        else if (availability < 95) fair++;
        else if (item.health === health.gray) unknown++;
        else good++;
      });
      Object.assign(dataRef.current.donut, { good, fair, bad, unknown });
    }
    dataRef.current.donutData = getDonutData(
      availDonutConfig.text,
      dataRef.current.donut,
    );
    dataRef.current.donutText = getDonutText(
      availDonutConfig.title,
      dataRef.current.donut,
    );
    data = data.filter((item) => item.health !== health.gray);
    const tableRecords = data.slice(0, MAX_ROWS);
    const circuitTableData = tableRecords.map((item) => ({
      ...item,
      availability_previous:
        item.availability_previous === defaultCellValue
          ? 0
          : item.availability_previous,
      availability:
        item.availability === defaultCellValue ? 0 : item.availability,
      tooltip: {
        name: item.circuit_display_name,
        current: item.availability === defaultCellValue ? 0 : item.availability,
        previous:
          item.availability_previous === defaultCellValue
            ? 0
            : item.availability_previous,
      },
      overlay: {
        current: {
          value: item.availability === defaultCellValue ? 0 : item.availability,
        },
        previous: {
          value:
            item.availability_previous === defaultCellValue
              ? 0
              : item.availability_previous,
        },
        total: { value: 100 },
      },
    }));
    if (reporting)
      dataRef.current.topCircuits = data.slice(-MAX_ROWS).map((item) => ({
        ...item,
        availability:
          item.availability === defaultCellValue ? 0 : item.availability,
        overlay: {
          current: {
            value:
              item.availability === defaultCellValue ? 0 : item.availability,
          },
          previous: {
            value:
              item.availability_previous === defaultCellValue
                ? 0
                : item.availability_previous,
          },
          total: { value: 100 },
        },
      }));
    availTableConfig.columns[1].tooltip = !reporting;
    setTableData(circuitTableData);
    setLoader(false);
  }, [
    globalFilter.selectedSite,
    globalFilter.circuitsMasterTable.loader,
    reportData.loader,
  ]);

  useEffect(() => {
    setselectedSiteId(globalFilter.selectedSite);
  }, [globalFilter.selectedSite]);

  useEffect(() => {
    if (!reporting)
      dataRef.current.tableConfig.columns[0].onClick = (cellValue, rowData) =>
        circuitNameClick(navigate, rowData);
  }, []);

  const setBandwithSelected = () => {
    setLocalStorageFlag("bandForecastingTab", true);
    navigate(routeURLs.bwf);
  };

  const handleZipCSVExport = () => {
    if (!canExportCsv) return;

    const fileDateTime = displayDateTime(new Date(), "yyyy-MM-dd hh mm ss aa");
    const overviewFileName = tViews("a11yLabels.overviewPageTitleLabel");
    const circuitsFileName = tViews("circuitViewCircuits");
    const donutLabels = availDonutConfig.text;
    const tableColumns = availTableConfig.columns;
    const donutData = dataRef.current.donut;
    const availabilityLabel = tViews("circuit.ctAvail");
    const previousAvailabilityLabel =
      dataRef.current.chartTooltipConfig.labels[1].label ||
      `${tViews("applicationOverviewLabelAvailabilityOn")} ` +
        timeFilterConfig[globalFilter.timePeriodSelected].toolTip;
    const totalAvailabilityLabel = tableColumns[2].title;
    const currentTotalAvailabilityLabel = `${totalAvailabilityLabel} - ${availabilityLabel}`;
    const previousTotalAvailabilityLabel = `${totalAvailabilityLabel} - ${previousAvailabilityLabel}`;
    const tableHeaders = [
      tableColumns[0].title,
      availabilityLabel,
      `${availabilityLabel} ${tViews("trendingAppsChange")}`,
      currentTotalAvailabilityLabel,
      previousTotalAvailabilityLabel,
    ];

    exportCsvZip({
      zipName: `${overviewFileName}_${circuitsFileName}_${fileDateTime}.zip`,
      files: [
        {
          name: `${circuitsFileName}_${tViews("a11yLabels.donut")}_${availabilityLabel}_${tViews("a11yLabels.data")}_${fileDateTime}.csv`,
          headers: [
            availabilityLabel,
            tViews("applicationTableViewCircuitCount"),
          ],
          rows: [
            [donutLabels.good.tooltip, donutData.good || 0],
            [donutLabels.fair.tooltip, donutData.fair || 0],
            [donutLabels.bad.tooltip, donutData.bad || 0],
            [donutLabels.unknown.tooltip, donutData.unknown || 0],
          ],
        },
        {
          name: `${tViews("bottomAvailableCircuits")}_${tViews("optimization.csvLabels.tableCsvTitle")}_${fileDateTime}.csv`,
          headers: tableHeaders,
          rows: tableData.length
            ? tableData.map((row) => [
                row.circuit_display_name,
                formatNumber(row.availability, "percent"),
                formatNumber(row.availability_change || 0, "changePercent"),
                formatNumber(row.tooltip?.current, "percent"),
                formatNumber(row.tooltip?.previous, "percent"),
              ])
            : [Array(tableHeaders.length).fill(tViews("noDataAvailable"))],
        },
      ],
    });
  };

  return (
    <HbrCard
      className={
        css[reporting ? "circuit-dashlet-reporting" : "circuit-dashlet"]
      }
      data-cy="circuit-card-view"
      data-testid="circuit-dashlet"
      container={true}
    >
      <div className="circuit-dashlet-view" data-cy="circuit-view">
        {!reporting && (
          <div className="title-block">
            <div
              className="header-circuit-content hbr-type-h2"
              role="heading"
              aria-level={"2"}
            >
              {tViews("circuitViewCircuits")}
            </div>
            <div className="circuit-view-actions">
              {bwfAccess.available === true && (
                <HbrButton variant="text" onClick={setBandwithSelected}>
                  {tViews("circuitViewBandwidthForecast")}
                </HbrButton>
              )}
              <HbrButton
                variant="text"
                onClick={() => navigate(routeURLs.circuits)}
                aria={{ label: tViews("a11yLabels.overviewCircuitDetails") }}
              >
                {tViews("circuitViewDetails")}
              </HbrButton>
              {canExportCsv && (
                <HbrTooltip
                  content={tViews("a11yLabels.zipDownloadTooltipText")}
                >
                  <HbrIconButton
                    size="20px"
                    name="file-csv"
                    data-testid="overview-circuits-csv-btn"
                    onClick={handleZipCSVExport}
                    arialabel={tViews("a11yLabels.csvZipDownloadLabel")}
                  />
                </HbrTooltip>
              )}
            </div>
          </div>
        )}
        {isCircuitViewLoading ? (
          <div
            className="spinner-component-circuit-view"
            data-cy="spinner-component-circuit-view"
          >
            <Spinner />
          </div>
        ) : dataRef.current.error ? (
          <ErrorComponent
            {...dataRef.current.error}
            width={"210px"}
            className={
              reporting ? "one-line-dashlet-error" : "small-dashlet-error"
            }
          />
        ) : (
          <div className="content-block">
            <div className={css["custom-legend-list"]}>
              {reporting && (
                <div className="title-block hbr-type-h3">
                  {reportName === "link_availability"
                    ? tViews("linkAvailabilityLabel")
                    : tViews("circuitAvailabilityBreakdown")}
                </div>
              )}
              <div>
                <HarborDonut
                  data={dataRef.current.donutData}
                  donutLabel={tViews("circuitsLabel")}
                />
                <div className="legend-circuit-view">
                  <QoeListComponent
                    reporting={reporting}
                    overviewPage={true}
                    iconType={[COLOR.GOOD, COLOR.FAIR, COLOR.POOR, COLOR.GRAY]}
                    qoeScore={
                      reporting
                        ? [
                            dataRef.current.donut.good,
                            dataRef.current.donut.fair,
                            dataRef.current.donut.bad,
                            dataRef.current.donut.unknown,
                          ]
                        : [0, 0, 0, 0]
                    }
                    qoeLabels={[
                      availDonutConfig.text.good.title,
                      availDonutConfig.text.fair.title,
                      availDonutConfig.text.bad.title,
                      availDonutConfig.text.unknown.title,
                    ]}
                    tooltipText={tViews("circuit.unknownAvailTtip")}
                    position={3}
                    ellipsis={true}
                  />
                </div>
              </div>
            </div>
            {/*** Rendered in Executive Summary Report Circuit widget : top available circuits* */}
            {reporting && reportName !== "link_availability" && (
              <div className="table-chartview">
                <div className="title-circuit-dashlet hbr-type-h3">
                  {tViews("topAvailableCircuits")}
                </div>
                <DashletTable
                  config={dataRef.current.tableTopConfig}
                  data={dataRef.current.topCircuits}
                  timeFilterType={reportData.query.timeFilterType}
                />
              </div>
            )}
            {/*** Rendered in Overview Circuit widget : Bottom available circuits* */}
            {!reporting && reportName !== "link_availability" && (
              <div className="table-chartview">
                <div
                  className="title-circuit-dashlet hbr-type-h3"
                  role="heading"
                  aria-level={"3"}
                >
                  {tViews("bottomAvailableCircuits")}
                </div>
                <DashletTable
                  config={dataRef.current.tableConfig}
                  data={tableData}
                  timeFilterType={
                    reporting
                      ? reportData.query.timeFilterType
                      : globalFilter.timePeriodSelected
                  }
                  chartTooltip={!reporting}
                  chartConfig={dataRef.current.chartTooltipConfig}
                  source={getSourceCircuit([])}
                  columnsName={columnsCircuitName()}
                  style={{ height: "40px", width: "380px" }}
                ></DashletTable>
              </div>
            )}
          </div>
        )}
      </div>
    </HbrCard>
  );
};

CircuitView.propTypes = {
  globalFilter: PropTypes.object.isRequired,
  reporting: PropTypes.bool,
  reportName: PropTypes.any,
  reportData: PropTypes.object,
};

export default AppHOC(CircuitView);
